"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Leaf, ArrowRight } from "lucide-react";
import ProductDetail from "../../ProductDetail/ProductDetail";
import { cn } from "@/lib/utils";
export default function FeaturedGuardianSeries() {
  const [posts, setPosts] = React.useState<any[]>([]);
  const [categories, setCategories] = React.useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);

  //  核心修正：主狀態完全與網址的 Hash 同步控制
  const [activeProductSlug, setActiveProductSlug] = useState<string | null>(null);

  //  核心修正：監聽瀏覽器歷史紀錄變更 (完美防護手機左滑退出)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash; // 取得如 "#product-hd5081"
      if (hash && hash.startsWith("#product-")) {
        const slug = hash.replace("#product-", "");
        setActiveProductSlug(slug);
      } else {
        // 當使用者在手機上左滑或點擊瀏覽器返回時，Hash 消失，這裡會自動關閉彈窗
        setActiveProductSlug(null);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // 初始化執行一次，確保直連網址能直接開彈窗

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  //  核心修正：開啟彈窗時，推入網址歷史紀錄
  const openProductModal = (slug: string) => {
    window.location.hash = `product-${slug}`;
  };

  //  核心修正：關閉彈窗時，觸發瀏覽器返回 (藉此移除 Hash 並關閉)
  const closeProductModal = () => {
    if (window.location.hash) {
      window.history.back();
    }
  };
  React.useEffect(() => {
    const fetchPosts = fetch("/wp-json/wp/v2/flooring?_embed").then(res => res.json());
    const fetchCats = fetch("/wp-json/wp/v2/flooring_cat").then(res => res.json());
    Promise.all([fetchPosts, fetchCats]).then(([postsData, catsData]) => {
      if (Array.isArray(postsData)) setPosts(postsData);
      if (Array.isArray(catsData)) setCategories(catsData);
    }).catch(err => console.error("Error fetching flooring data:", err)).finally(() => setLoading(false));
  }, []);
  const colorCategories = categories.filter(cat => cat.name.includes("色系") && !cat.name.includes("挑選"));
  const filteredPosts = posts.filter(post => {
    if (selectedCategory === null) return true;
    return post.flooring_cat?.includes(selectedCategory);
  });
  return <section id="產品展示牆" className="bg-secondary/20 py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* 完美還原設計稿：左右大氣對齊佈局 */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 w-full">
          
          {/* 左側：精緻雙行標題文案 */}
          <div className="text-left max-w-xl">
            <div className="flex items-center gap-3 font-sans text-xl sm:text-2xl md:text-3xl font-medium text-[#3d3d3d] tracking-widest mb-2">
              <h2>從地板開始</h2>
              <span className="text-[#3d3d3d]/40 font-light text-lg">│</span>
              <span className="text-[#cbb3a3] text-xs sm:text-sm font-light tracking-widest uppercase pt-1">
                / FLOORING COLOR
              </span>
            </div>
            
            <div className="relative inline-block">
              <div className="absolute left-0 bottom-0.5 w-full h-2.5 bg-[#cbb3a3]/50 -z-10" />
              <h3 className="font-sans text-xl sm:text-2xl md:text-3xl font-medium text-[#3d3d3d] tracking-widest leading-none">
                鋪陳自然愜意的生活樣貌
              </h3>
            </div>
          </div>

          {/* 右側：純色系分類按鈕群容器 */}
          <div className="flex flex-wrap items-center justify-start md:justify-end gap-x-2.5 gap-y-3 pb-1 w-full md:max-w-md lg:max-w-xl xl:max-w-2xl">
            <Button variant={selectedCategory === null ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(null)} className={cn("rounded-full px-5 text-xs tracking-widest transition-all duration-300 shrink-0 border-[#f7f4f0] bg-[#f7f4f0] text-[#3d3d3d] hover:bg-[#604f45]/10", selectedCategory === null && "bg-[#604f45] text-white hover:bg-[#504139] border-[#604f45]")}>
              全部
            </Button>

            {colorCategories.map(cat => <Button key={cat.id} variant={selectedCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat.id)} className={cn("rounded-full px-5 text-xs tracking-widest transition-all duration-300 shrink-0 border-[#f7f4f0] bg-[#f7f4f0] text-[#3d3d3d] hover:bg-[#604f45]/10", selectedCategory === cat.id && "bg-[#604f45] text-white hover:bg-[#504139] border-[#604f45]")}>
                {cat.name}
              </Button>)}
          </div>

        </div>

        {/* 產品展示網格 */}
        {loading ? <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="aspect-[4/5] bg-muted/40 animate-pulse rounded-sm" />)}
          </div> : filteredPosts.length > 0 ? <div key={selectedCategory} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {filteredPosts.map((post, index) => <Card key={post.id} className={cn("group relative flex flex-col bg-card rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all border border-border/60 cursor-pointer", "animate-in fade-in slide-in-from-bottom-8 duration-700 fill-mode-both")} style={{
          animationDelay: `${index * 100}ms`
        }}
        //  核心修正：點擊改為推入網址 Hash 路由
        onClick={() => openProductModal(post.slug)}>
                <div className="relative aspect-[4/5] overflow-hidden bg-muted/10">
                  <AspectRatio ratio={4 / 5} className="w-full h-full">
                    <img src={post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || ""} alt={post.title.rendered} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                  </AspectRatio>
                  <div className="absolute inset-x-0 bottom-0 p-3 translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 z-20 bg-gradient-to-t from-black/60 to-transparent">
                    <Button className="w-full bg-white text-black hover:bg-primary hover:text-white rounded-none py-2 font-sans text-[10px] font-bold">
                      快速查看詳情 / DETAIL
                    </Button>
                  </div>
                </div>

                <CardContent className="p-4 flex flex-col flex-grow text-left">
                  <h3 className="font-sans font-bold text-sm md:text-base text-foreground line-clamp-1 group-hover:text-primary transition-colors mb-1" dangerouslySetInnerHTML={{
              __html: post.title.rendered
            }} />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-3 italic">
                    {categories.find(c => post.flooring_cat?.includes(c.id))?.name || "Series"}
                  </p>
                  <div className="mt-auto inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:gap-2 transition-all uppercase tracking-widest">
                    了解更多 <ArrowRight className="size-3" />
                  </div>
                </CardContent>
              </Card>)}
          </div> : <div className="py-32 text-center flex flex-col items-center gap-4">
            <Leaf className="size-12 text-muted/30" />
            <p className="text-muted-foreground tracking-widest italic font-sans">目前尚無此系列產品 No products available</p>
          </div>}

        {/*  核心修正：主頁面乾乾淨淨，只宣告一列 ProductDetail 並把控制與關閉權限傳進去 */}
        <ProductDetail slug={activeProductSlug} onClose={closeProductModal} />
      </div>
    </section>;
}